import { Location } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { FullscreenLoaderService } from './core/components/fullscreen-loader/fullscreen-loader.service';
import { ConfigService } from './core/services/config/config.service';
import { LangService } from './core/services/lang.service';

@Component({
  selector: 'sliqpay-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly configService = inject(ConfigService);
  private readonly langService = inject(LangService);
  private readonly fullscreenLoaderService = inject(FullscreenLoaderService);
  private readonly location = inject(Location);
  private readonly loadingBar = inject(LoadingBarService);
  private readonly destroyRef = inject(DestroyRef);

  title = 'DentiCare front';
  windowWidth$ = new BehaviorSubject(1280);
  wrapperClass$ = new BehaviorSubject(null);
  fullscreenLoaderState$ = this.fullscreenLoaderService.fullscreenUiState$();

  isLoading$ = this.loadingBar.useRef('http').value$;

  readonly loadingBarColor = computed(() =>
    getComputedStyle(document.documentElement).getPropertyValue('--color-brand-primary').trim()
  );

  config$!: Observable<any>;
  lang$!: Observable<any>;

  ngOnInit() {
    this.resetSmsJourneyStates();

    // Setup config
    this.config$ = this.configService.setConfigInStore();
    this.lang$ = this.langService.setLangConfigInStore();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        tap(() => this.resetScrollPosition()),
        map(() => this.route),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        switchMap(route => route.data),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(data => {
        this.wrapperClass$.next(data['wrapperClass'] ?? '');
      });
  }

  onResize(event: any) {
    this.windowWidth$.next(event.target.innerWidth);
  }

  private resetSmsJourneyStates() {
    const currentPath = this.location.path(true);
    const currentState = window.history.state || {};

    this.location.replaceState(
      currentPath,
      '', // keep query string if needed
      { ...currentState, isFromSmsJourney: false }
    );

    sessionStorage.removeItem('proposaLoaderDetailsHeader');
  }

  private resetScrollPosition(): void {
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.querySelectorAll<HTMLElement>('*').forEach(el => {
        if (el.scrollTop > 0) el.scrollTop = 0;
      });
    });
  }
}
